import React, { useState, useEffect } from 'react';
import { CardComponentProps } from '@/lib/cards';

/**
 * Roles & Permissions Card
 * Shows user roles and permissions (system_admin only)
 */
export const RolesPermissionsCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const mockData = {
          roles: ['org_admin', 'subscriber'],
          permissions: [
            'read:users',
            'write:users',
            'read:billing',
            'read:audit_log',
          ],
        };
        setData(mockData);
      } catch (err) {
        onError?.(err instanceof Error ? err : new Error(String(err)));
      } finally {
        setLoading(false);
      }
    }

    loadData();
  }, [onError]);

  if (loading) return <div style={{ padding: '1rem', color: '#666' }}>Loading...</div>;
  if (!data) return null;

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ marginBottom: '1.5rem' }}>
        <strong>Roles:</strong>
        <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          {data.roles.map((role: string) => (
            <span
              key={role}
              style={{
                padding: '0.25rem 0.75rem',
                backgroundColor: '#e7f3ff',
                border: '1px solid #0066cc',
                borderRadius: '3px',
                fontSize: '0.85rem',
              }}
            >
              {role}
            </span>
          ))}
        </div>
      </div>

      <div>
        <strong>Permissions:</strong>
        <ul style={{ marginTop: '0.5rem', paddingLeft: '1.5rem' }}>
          {data.permissions.map((perm: string) => (
            <li key={perm} style={{ fontSize: '0.9rem', color: '#555' }}>
              {perm}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};

RolesPermissionsCard.displayName = 'RolesPermissionsCard';

/**
 * Admin Actions Card
 * Provides system admin actions on user account
 */
export const AdminActionsCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const handleAction = async (action: string) => {
    try {
      setActionInProgress(action);
      console.log(`Executing: ${action}`);
      await new Promise((resolve) => setTimeout(resolve, 600));
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionInProgress(null);
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {card.cardActionsList.map((action: string) => (
          <button
            key={action}
            onClick={() => handleAction(action)}
            disabled={actionInProgress !== null}
            style={{
              padding: '0.6rem 1rem',
              backgroundColor: action === 'Suspend user' ? '#dc3545' : '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: actionInProgress !== null ? 'not-allowed' : 'pointer',
              opacity: actionInProgress !== null ? 0.6 : 1,
              textAlign: 'left',
              fontWeight: 500,
            }}
          >
            {action === actionInProgress && '⟳ '}
            {action}
          </button>
        ))}
      </div>
    </div>
  );
};

AdminActionsCard.displayName = 'AdminActionsCard';

/**
 * Org Actions Card
 * Provides org admin actions on user within organisation
 */
export const OrgActionsCard: React.FC<CardComponentProps> = ({
  card,
  userContext,
  onError,
}) => {
  const [actionInProgress, setActionInProgress] = useState<string | null>(null);

  const handleAction = async (action: string) => {
    try {
      setActionInProgress(action);
      console.log(`Executing: ${action}`);
      await new Promise((resolve) => setTimeout(resolve, 500));
    } catch (err) {
      onError?.(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setActionInProgress(null);
    }
  };

  return (
    <div style={{ padding: '1rem' }}>
      <div style={{ display: 'grid', gap: '0.5rem' }}>
        {card.cardActionsList.map((action: string) => (
          <button
            key={action}
            onClick={() => handleAction(action)}
            disabled={actionInProgress !== null}
            style={{
              padding: '0.6rem 1rem',
              backgroundColor: action === 'Remove from org' ? '#dc3545' : '#0066cc',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: actionInProgress !== null ? 'not-allowed' : 'pointer',
              opacity: actionInProgress !== null ? 0.6 : 1,
              textAlign: 'left',
              fontWeight: 500,
            }}
          >
            {action === actionInProgress && '⟳ '}
            {action}
          </button>
        ))}
      </div>
    </div>
  );
};

OrgActionsCard.displayName = 'OrgActionsCard';
